// ./src/components/features/EventVendors/EventVendorsList/EventVendorsList.tsx
//
// ROADMAP #170 — the vendors working an event: a table on desktop, cards below (buttons on a tablet, a swipe on a phone).
// Each row says who the vendor is and what they do here, who to talk to, how far their documents are ("3 of 5 approved"),
// what needs a look (documents to review, overdue, expiring before the event), and whether their link was sent. A row
// opens the vendor's own page.
import React, { useCallback, useMemo } from "react";
import { Paper, Stack, Group, Text, Box, ActionIcon, Tooltip, Badge, Skeleton, Table, UnstyledButton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { EventVendor, EventVendorEmailDelivery } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  TableDateTime,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
} from "@/components/generic";

import { owedLine, personName, progressLine, vendorEmailWords } from "../vendorUi";

export interface EventVendorsListProps {
  data: EventVendor[];
  loading: boolean;
  /** ⚠ REQUIRED — whether the FIRST read has come back (the skeleton belongs to it alone). ▶ check-list-states */
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  /** True when a search is narrowing the list — changes what "empty" means. */
  hasQueryNarrowing: boolean;
  onOpen: (vendor: EventVendor) => void;
  onDelete?: (vendor: EventVendor) => void;
  /** First-run empty state's action. */
  onAdd?: () => void;
  isRemoving: (id?: string) => boolean;
  highlightedId?: string | null;
  /** #202 — what became of the emails, by event vendor. Absent while unread or when the read failed: no badge says nothing either way. */
  emailDelivery?: Map<string, EventVendorEmailDelivery>;
}

interface RowProps {
  vendor: EventVendor;
  delivery?: EventVendorEmailDelivery;
  onOpen: (vendor: EventVendor) => void;
  onDelete?: (vendor: EventVendor) => void;
  highlighted: boolean;
  isDeleting: boolean;
}

const VendorCell: React.FC<{ vendor: EventVendor }> = ({ vendor }) => {
  const { t } = useTranslation(["vendors"]);

  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Group gap={6} wrap="nowrap">
        <Text size="md" fw={600} truncate>
          {vendor.vendorName || t("vendors:list.unnamed", { defaultValue: "Unnamed vendor" })}
        </Text>
        {vendor.vendorRemoved ? (
          <Badge size="sm" variant="light" color="gray" style={{ flexShrink: 0 }}>
            {t("vendors:list.removedFromLibrary", { defaultValue: "Removed from Library" })}
          </Badge>
        ) : null}
      </Group>
      {vendor.serviceDescription ? (
        <Text size="md" c="dimmed" truncate>
          {vendor.serviceDescription}
        </Text>
      ) : null}
    </Stack>
  );
};

const PeopleCell: React.FC<{ vendor: EventVendor }> = ({ vendor }) => {
  const { t } = useTranslation(["vendors"]);
  const people = vendor.people ?? [];

  if (people.length === 0) {
    return (
      <Text size="md" c="orange">
        {t("vendors:list.noPeople", { defaultValue: "Nobody to contact yet" })}
      </Text>
    );
  }

  const first = personName(people[0]) || people[0].email || "";

  return (
    <Text size="md" truncate>
      {people.length > 1 ? t("vendors:list.peopleMore", { defaultValue: "{{name}} +{{n}}", name: first, n: people.length - 1 }) : first}
    </Text>
  );
};

/** "3 of 5 approved", and what needs a look — to review, overdue / expiring. */
const DocumentsCell: React.FC<{ vendor: EventVendor; delivery?: EventVendorEmailDelivery }> = ({ vendor, delivery }) => {
  const { t } = useTranslation(["vendors"]);
  const c = vendor.counts;
  const notEmailed = !vendor.linkSentAt && !!owedLine(vendor);
  const email = delivery ? vendorEmailWords(delivery.state, delivery.recipients?.find((r) => r.state === delivery.state)?.event) : null;

  return (
    <Stack gap={4} style={{ minWidth: 0 }}>
      <Text size="md" c={c && c.total > 0 && c.approved === c.total ? "green" : undefined}>
        {progressLine(vendor)}
      </Text>
      {owedLine(vendor) ? (
        <Text size="md" c="dimmed" lineClamp={2}>
          {t("vendors:list.stillOwed", { defaultValue: "Still owed: {{names}}", names: owedLine(vendor) })}
        </Text>
      ) : null}
      {notEmailed || email ? (
        <Group gap={6}>
          {notEmailed ? (
            <Badge size="sm" variant="light" color="yellow">
              {t("vendors:list.notEmailed", { defaultValue: "Not emailed yet" })}
            </Badge>
          ) : null}
          {email ? (
            <Badge size="sm" variant="light" color={email.color}>
              {t("vendors:list.email", { defaultValue: "Email: {{state}}", state: email.label })}
            </Badge>
          ) : null}
        </Group>
      ) : null}
      {c && (c.submitted > 0 || c.attention > 0) ? (
        <Group gap={6}>
          {c.submitted > 0 ? (
            <Badge size="sm" variant="light" color="blue">
              {t("vendors:list.toReview", { defaultValue: "{{n}} to review", n: c.submitted })}
            </Badge>
          ) : null}
          {c.attention > 0 ? (
            <Badge size="sm" variant="light" color="red">
              {t("vendors:list.attention", { defaultValue: "{{n}} need a look", n: c.attention })}
            </Badge>
          ) : null}
        </Group>
      ) : null}
    </Stack>
  );
};

const RowActions: React.FC<RowProps> = ({ vendor, onOpen, onDelete, isDeleting }) => {
  const { t } = useTranslation(["vendors", "common"]);

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      <Tooltip label={t("vendors:actions.open", { defaultValue: "Open" })} withArrow>
        <ActionIcon
          variant="subtle"
          color="neutral"
          size="lg"
          disabled={isDeleting}
          onClick={() => onOpen(vendor)}
          aria-label={t("vendors:actions.open", { defaultValue: "Open" })}
        >
          <AppIcon iconName="ChevronRight" size={18} />
        </ActionIcon>
      </Tooltip>
      {onDelete && recordHasRight(vendor, "remove") ? (
        <Tooltip label={t("vendors:actions.remove", { defaultValue: "Remove from the event" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="error"
            size="lg"
            loading={isDeleting}
            onClick={() => onDelete(vendor)}
            aria-label={t("vendors:actions.remove", { defaultValue: "Remove from the event" })}
          >
            <AppIcon iconName="Trash" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
};

const VendorTableRow: React.FC<RowProps> = (p) => (
  <Table.Tr
    style={{
      opacity: p.isDeleting ? 0.5 : 1,
      backgroundColor: p.highlighted ? HIGHLIGHT_ROW_BG : undefined,
      transition: "background-color 150ms ease, opacity 150ms ease",
    }}
  >
    <Table.Td>
      <UnstyledButton onClick={() => p.onOpen(p.vendor)} style={{ minWidth: 0, width: "100%" }}>
        <VendorCell vendor={p.vendor} />
      </UnstyledButton>
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <PeopleCell vendor={p.vendor} />
    </Table.Td>
    <Table.Td style={{ width: 240 }}>
      <DocumentsCell vendor={p.vendor} delivery={p.delivery} />
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <TableDateTime value={p.vendor.linkSentAt ?? null} />
    </Table.Td>
    <Table.Td style={{ width: 110 }}>
      <RowActions {...p} />
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const VendorCardComponent: React.FC<CardProps> = (p) => {
  const { t } = useTranslation(["vendors", "common"]);
  const { vendor } = p;

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    if (p.onDelete && recordHasRight(vendor, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("vendors:actions.removeShort", { defaultValue: "Remove" }),
        color: "red",
        onClick: () => p.onDelete?.(vendor),
        loading: p.isDeleting,
        disabled: p.isDeleting,
      });
    }

    return actions;
  }, [p, vendor, t]);

  const cardBody = (showActions: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={p.isDeleting ? 0.5 : 1}
      style={{ transition: "opacity 150ms ease", ...(p.highlighted ? HIGHLIGHT_STYLE : {}) }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        <UnstyledButton onClick={() => p.onOpen(vendor)} style={{ flex: 1, minWidth: 0 }}>
          <VendorCell vendor={vendor} />
          <Box mt={6}>
            <PeopleCell vendor={vendor} />
          </Box>
          <Box mt={6}>
            <DocumentsCell vendor={vendor} delivery={p.delivery} />
          </Box>
        </UnstyledButton>
        {showActions && <RowActions {...p} />}
      </Group>
    </Paper>
  );

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={p.forceClose}
            onForceCloseComplete={p.onForceCloseComplete}
            onSwipeStateChange={p.onSwipeStateChange}
            disabled={p.isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false)}
          </SwipeableCard>
        ) : (
          cardBody(false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true)}</Box>
    </>
  );
};

const VendorCard = React.memo(VendorCardComponent);

const EventVendorsListComponent: React.FC<EventVendorsListProps> = ({
  data,
  loading,
  isLoaded,
  loadingMore,
  hasMore,
  onLoadMore,
  hasQueryNarrowing,
  onOpen,
  onDelete,
  onAdd,
  isRemoving,
  highlightedId,
  emailDelivery,
}) => {
  const { t } = useTranslation(["vendors", "common"]);
  const { isDesktop } = useAppEnvironment();
  // The tablet card's row buttons (Open + Remove) — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onOpen, onDelete);
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleDelete = useCallback(
    (vendor: EventVendor) => {
      closeAllCards();
      onDelete?.(vendor);
    },
    [onDelete, closeAllCards]
  );

  const header = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{t("vendors:list.columns.vendor", { defaultValue: "Vendor" })}</Table.Th>
        <Table.Th style={{ width: 200 }}>{t("vendors:list.columns.people", { defaultValue: "Contact" })}</Table.Th>
        <Table.Th style={{ width: 240 }}>{t("vendors:list.columns.documents", { defaultValue: "Documents" })}</Table.Th>
        <Table.Th style={{ width: 150 }}>{t("vendors:list.columns.lastEmailed", { defaultValue: "Last emailed" })}</Table.Th>
        <Table.Th style={{ width: 110 }} />
      </Table.Tr>
    </Table.Thead>
  );

  // ⚠ The FIRST read only. ▶ npm run check-list-states
  if (!isLoaded) {
    return (
      <Stack gap="xs" aria-busy="true">
        <Box visibleFrom="md">
          <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              {header}
              <Table.Tbody>
                {[0, 1, 2, 3].map((i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Skeleton height={16} width="60%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width="70%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width="60%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={90} />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap" justify="flex-end">
                        <Skeleton circle height={34} width={34} />
                        <Skeleton circle height={34} width={34} />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Box>
        {[0, 1, 2].map((i) => (
          // The phone card: the vendor (+ service), the contact, the documents.
          <Paper key={`p-${i}`} withBorder radius="md" p="md" hiddenFrom="sm">
            <CardSkeletonLines count={3} gap={6} />
          </Paper>
        ))}
        {[0, 1, 2].map((i) => (
          <Paper key={`t-${i}`} withBorder radius="md" p="md" visibleFrom="sm" hiddenFrom="md">
            <Group gap="md" wrap="nowrap" justify="space-between">
              <CardSkeletonLines count={3} gap={6} />
              <CardSkeletonActions count={cardActions} />
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  }

  if (data.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("vendors:list.noMatch", { defaultValue: "No vendors match your search." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("vendors:list.emptyTitle", { defaultValue: "No vendors on this event yet" })}
          description={t("vendors:list.emptyBody", {
            defaultValue:
              "Add the caterer, the sound company, the tent rental… then list the documents each one owes — a certificate of insurance, a permit — and send them a link to upload them. No account needed.",
          })}
          iconName="Certificate"
          actionLabel={onAdd ? t("vendors:actions.add", { defaultValue: "Add a vendor" }) : undefined}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  const rowProps = (vendor: EventVendor): RowProps => ({
    vendor,
    onOpen,
    onDelete: onDelete ? handleDelete : undefined,
    highlighted: vendor.id === highlightedId,
    isDeleting: isRemoving(vendor.id),
    delivery: vendor.id ? emailDelivery?.get(vendor.id) : undefined,
  });

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            {header}
            <Table.Tbody>
              {data.map((vendor) => (
                <VendorTableRow key={vendor.id} {...rowProps(vendor)} />
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {loadMore}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((vendor) => (
          <VendorCard
            key={vendor.id}
            {...rowProps(vendor)}
            forceClose={shouldForceClose(vendor.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(vendor.id || "")}
            onSwipeStateChange={(isOpen) => isOpen && openCard(vendor.id || "")}
          />
        ))}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const EventVendorsList = React.memo(EventVendorsListComponent);
